/**
 * The three things the Knowledge Archive body shows instead of a graph —
 * ROUTE_INVENTORY §2.10 #46, #47, #48.
 *
 * ## `GraphErrorState` is unreachable through the page
 *
 * Worth knowing before writing a story for it: `useKnowledgeArchiveGraph`
 * CATCHES a failed request and falls back to `buildMockKnowledgeGraph()`, so
 * `error` is never populated in any data mode and the page renders a synthetic
 * graph instead (§2.10 #5 — that fallback is itself the storied behaviour).
 * This component is therefore only reachable at component grain, which is why
 * it lives here as a plain props component rather than inline on the page.
 *
 * ## The empty state is three states
 *
 * Unfiltered-empty is a new user and gets an explanation of how concepts
 * arrive. Filtered-empty is a dead end the user built and gets a way out.
 * Paused-only is called out separately because it is the likeliest empty
 * graph of the three — "Paused only" with nothing paused — and the generic
 * "no match" copy would send someone hunting through filters that are working
 * correctly.
 */

import { Loader2, Network, X } from 'lucide-react';
import { Button } from '@ai-tutor/ui';

export function GraphLoadingState() {
  return (
    <div className='flex h-full flex-col items-center justify-center gap-3 p-8 text-center'>
      <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      <p className='text-xs text-muted-foreground'>
        Loading your knowledge graph…
      </p>
    </div>
  );
}

interface GraphEmptyStateProps {
  /** Any filter active — switches from "nothing yet" to "nothing matches". */
  hasFilters: boolean;
  /** `?archived=1` specifically, which gets its own copy. */
  pausedOnly?: boolean;
  onClear: () => void;
}

export function GraphEmptyState({
  hasFilters,
  pausedOnly,
  onClear,
}: GraphEmptyStateProps) {
  if (hasFilters) {
    const title = pausedOnly
      ? 'No paused concepts'
      : 'No concepts match these filters';
    const body = pausedOnly
      ? 'Concepts you pause from their detail panel show up here. Nothing is paused right now.'
      : 'Try removing some filters to see more of your knowledge graph.';
    return (
      <div className='flex h-full flex-col items-center justify-center p-8 text-center'>
        <Network className='mb-3 h-10 w-10 text-gray-300' />
        <h3 className='mb-1 text-sm font-semibold text-foreground'>{title}</h3>
        <p className='mb-4 text-xs text-muted-foreground'>{body}</p>
        <Button
          variant='outline'
          size='sm'
          onClick={onClear}
          className='max-md:h-11'
        >
          <X className='mr-1 h-3 w-3' /> Clear filters
        </Button>
      </div>
    );
  }
  return (
    <div className='flex h-full flex-col items-center justify-center p-8 text-center'>
      <Network className='mb-3 h-10 w-10 text-gray-300' />
      <h3 className='mb-1 text-sm font-semibold text-foreground'>
        No concepts yet
      </h3>
      <p className='text-xs text-muted-foreground'>
        Generate a lesson and concepts will appear here once it syncs.
      </p>
    </div>
  );
}

export function GraphErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className='flex h-full flex-col items-center justify-center p-8 text-center'>
      <p className='mb-3 text-sm text-destructive-emphasis'>
        Couldn&apos;t load your knowledge graph.
      </p>
      <Button variant='outline' size='sm' onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
